import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { delay } from 'redux-saga';
import { Action } from 'redux-actions';

import recentSearchesSagas from './recentSearches/sagas';
import { REDIRECT_TO_FAST_SEARCH, RedirectToFastSearch, SUBMIT_FORM, SubmitFormAction } from './actions';
import { getSearchParameters } from './selectors';
import { createFastSearchURL } from '../utils';
import { SET_SEGMENTS, SetSegmentsAction, setLocation } from './segments/actions';
import {
	AviaSearchParameters,
	LocationsDocument,
	LocationsQuery,
	LocationsQueryVariables,
	RouteType,
	SegmentSearchParameter
} from '@websky/graphql';
import { LocationType } from '../types';
import { createClient } from '../../graphql-client';
import { FunnelParametersType, SearchGoal, reachGoal } from '../../analytics';
import { isOfferHotels } from './offerHotels/selectors';
import passengersSaga from './passengers/sagas';
import { SEARCH_FORM_STATE_KEY, set } from '../../cache';
import { getLocale } from '../../utils';
import { store } from '../components/Component';

const getLocation = async (query: string) => {
	const client = createClient();

	const request = await client.query<LocationsQuery, LocationsQueryVariables>({
		query: LocationsDocument,
		variables: {
			parameters: {
				query
			}
		}
	});

	return request?.data?.LocationSuggestions;
};

function* sendAnalyticsEvent() {
	const searchParameters: AviaSearchParameters = yield select(getSearchParameters);
	const isOfferHotelsActive: boolean = yield select(isOfferHotels);

	const from: SegmentSearchParameter = searchParameters.segments[0];
	const to: SegmentSearchParameter = searchParameters.segments[searchParameters.segments.length - 1];
	const isRT = from.departure.iata === to.arrival.iata;
	const goal = isOfferHotelsActive ? SearchGoal.SuccessfulSearchWithHotels : SearchGoal.SuccessfulSearchWithoutHotels;

	const funnelParameters: FunnelParametersType = {
		airport_from: from.departure.iata,
		airport_to: to.arrival.iata,
		date_start: from.date,
		date_end: to.date,
		passenger_type: searchParameters.passengers.map(passenger => `${passenger.count}${passenger.passengerType}`),
		route_type: isRT ? RouteType.RT : RouteType.OW,
		avia_operator: window.location.hostname,
		transport_leg: null,
		payment_type: null,
		auth_type: null,
		register_type: null,
		search_id: null
	};

	reachGoal(goal, { funnel_purchase: funnelParameters });
}

function* submitFormSaga() {
	yield takeLatest(SUBMIT_FORM, function* ({ payload: onSubmit }: SubmitFormAction) {
		const searchParameters: AviaSearchParameters = yield select(getSearchParameters);

		set(SEARCH_FORM_STATE_KEY, {
			locale: getLocale(),
			state: store.getState()
		});

		yield call(sendAnalyticsEvent);
		onSubmit(searchParameters);
	});
}

function* redirectToFastSearchSaga() {
	yield takeLatest(REDIRECT_TO_FAST_SEARCH, function* ({ payload }: Action<RedirectToFastSearch>) {
		const searchParameters: AviaSearchParameters = yield select(getSearchParameters);

		yield call(sendAnalyticsEvent);
		yield delay(500); // timeout for analytics request

		const fastSearchURL = `${payload.engineUrl}#/search/${createFastSearchURL(searchParameters)}`;

		if (payload.hotelsUrl) {
			window.open(
				payload.openOfferHotelsStrategy === 'newTab' ? yield payload.hotelsUrl : fastSearchURL,
				'_blank'
			);

			window.location.href =
				payload.openOfferHotelsStrategy === 'currentTab' ? yield payload.hotelsUrl : fastSearchURL;
		} else {
			window.location.href = fastSearchURL;
		}
	});
}

function* handleSetSegments() {
	yield takeLatest(SET_SEGMENTS, function* ({ payload: segments }: SetSegmentsAction) {
		if (!!segments.simpleRouteSegment?.arrival?.iata) {
			const suggest: LocationsQuery['LocationSuggestions'] = yield call(
				getLocation,
				segments.simpleRouteSegment.arrival.iata
			);

			if (suggest?.[0]?.cities?.[0].iata === segments.simpleRouteSegment.arrival.iata) {
				yield put(setLocation(suggest[0].cities[0], LocationType.Arrival));
			}
		}

		if (!!segments.simpleRouteSegment?.departure?.iata) {
			const suggest: LocationsQuery['LocationSuggestions'] = yield call(
				getLocation,
				segments.simpleRouteSegment.departure.iata
			);

			if (suggest?.[0]?.cities?.[0].iata === segments.simpleRouteSegment.departure.iata) {
				yield put(setLocation(suggest[0].cities[0], LocationType.Departure));
			}
		}
	});
}

export default function* rootSaga() {
	yield all([
		recentSearchesSagas(),
		submitFormSaga(),
		handleSetSegments(),
		redirectToFastSearchSaga(),
		passengersSaga()
	]);
}
